import type { ItemRecord } from '../types';
import { localSemanticAliases, normalizeText } from './semantic';

const searchCache = new WeakMap<ItemRecord, { candidate: string; tokens: string[] }>();

function editDistance(a: string, b: string, limit = 2) {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  if (Math.abs(a.length - b.length) > limit) return limit + 1;
  let previous = Array.from({ length: b.length + 1 }, (_, index) => index);

  for (let i = 1; i <= a.length; i += 1) {
    const current = [i];
    let rowMinimum = current[0];
    for (let j = 1; j <= b.length; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      current[j] = Math.min(current[j - 1] + 1, previous[j] + 1, previous[j - 1] + cost);
      rowMinimum = Math.min(rowMinimum, current[j]);
    }
    if (rowMinimum > limit) return limit + 1;
    previous = current;
  }
  return previous[b.length];
}

function tokenScore(query: string, candidate: string) {
  if (query === candidate) return 8;
  if (candidate.includes(query) || query.includes(candidate)) return 5;
  const longest = Math.max(query.length, candidate.length);
  return longest >= 5 && editDistance(query, candidate) <= 2 ? 2 : 0;
}

export function searchItems(items: ItemRecord[], query: string) {
  const normalizedQuery = normalizeText(query).slice(0, 240);
  if (!normalizedQuery) return items.toSorted((a, b) => b.createdAt.localeCompare(a.createdAt));

  const queryTokens = normalizedQuery.split(' ').filter(Boolean);
  return items.flatMap((item) => {
    const { candidate, tokens: candidateTokens } = searchEntry(item);
    let score = candidate.includes(normalizedQuery) ? 14 : 0;
    for (const token of queryTokens) {
      let best = 0;
      for (const candidateToken of candidateTokens) best = Math.max(best, tokenScore(token, candidateToken));
      score += best;
    }
    return score > 0 ? [{ item, score }] : [];
  }).toSorted((a, b) => b.score - a.score || b.item.updatedAt.localeCompare(a.item.updatedAt)).map(({ item }) => item);
}

function searchEntry(item: ItemRecord) {
  const cached = searchCache.get(item);
  if (cached) return cached;
  const candidate = normalizeText([
    item.name,
    item.name,
    item.locationNote,
    item.locationGroup,
    item.locationGroup,
    ...item.synonyms,
    ...item.aiSuggestions,
    ...localSemanticAliases(item.name, item.locationNote, item.locationGroup)
  ].join(' '));
  const entry = { candidate, tokens: candidate.split(' ').filter(Boolean) };
  searchCache.set(item, entry);
  return entry;
}
